/**
 * KpiRow — 4 horizontal KPI tiles, admin parity.
 *
 * Layout: icon-left (bare accent-tinted, no chip box), label uppercase eyebrow,
 * big tabular value, optional signed delta % with up/down arrow, sub-text below.
 */
import { ArrowDownRight, ArrowUpRight, Coins, MousePointerClick, Target, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';
import { formatCents, formatNumber } from '@/lib/format';
import type { DashboardMock } from '@/mocks/dashboard';

function deltaPct(curr: number, prev: number): number | null {
  if (!Number.isFinite(prev) || prev <= 0) return curr > 0 ? 100 : null;
  const d = ((curr - prev) / prev) * 100;
  return Number.isFinite(d) ? d : null;
}

interface TileProps {
  label: string;
  value: string;
  delta: number | null;
  sub?: string;
  icon: LucideIcon;
  accent: 'accent' | 'success' | 'warning' | 'info';
  tooltip?: string;
}

function Tile({ label, value, delta, sub, icon: Icon, accent, tooltip }: TileProps) {
  const accentVar =
    accent === 'success' ? 'var(--success)'
    : accent === 'warning' ? 'var(--warning)'
    : accent === 'info' ? 'var(--info)'
    : 'var(--accent-bright)';
  const up = delta != null && delta >= 0;
  return (
    <Card
      title={tooltip}
      className="transition-[border,box-shadow,transform] duration-[var(--duration-base)] ease-[var(--ease-out)] hover:border-[var(--border-strong)] hover:shadow-[var(--shadow-md)] hover:-translate-y-px"
    >
      <CardContent className="!p-4 flex items-start gap-3 min-w-0">
        <span
          className="shrink-0 inline-flex items-center justify-center h-10 w-10"
          style={{ color: accentVar }}
          aria-hidden
        >
          <Icon size={22} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
            {label}
          </div>
          <div className="flex items-baseline gap-2 mt-1 flex-wrap">
            <span
              className="text-[24px] md:text-[26px] font-bold tabular-nums leading-none text-[var(--text)]"
              style={{ letterSpacing: 'var(--tracking-tight)' }}
            >
              {value}
            </span>
            {delta != null && (
              <span
                className="inline-flex items-center gap-0.5 text-[11px] tabular-nums font-semibold"
                style={{ color: up ? 'var(--success)' : 'var(--danger)' }}
              >
                {up ? <ArrowUpRight size={11} strokeWidth={2.5} /> : <ArrowDownRight size={11} strokeWidth={2.5} />}
                {Math.abs(delta).toFixed(1)}%
              </span>
            )}
          </div>
          {sub && (
            <div className="text-[11px] text-[var(--text-quiet)] tabular-nums mt-1 truncate">{sub}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function KpiRow({ kpis }: { kpis: DashboardMock['kpis'] }) {
  const { clicks, leads, conversions, epcCents } = kpis;
  const cr = clicks.current > 0 ? ((conversions.current / clicks.current) * 100).toFixed(2) : '0.00';
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      <Tile
        label="Клики"
        value={formatNumber(clicks.current)}
        delta={deltaPct(clicks.current, clicks.previous)}
        sub={`за период · CR ${cr}%`}
        icon={MousePointerClick}
        accent="accent"
      />
      <Tile
        label="Лиды"
        value={formatNumber(leads.current)}
        delta={deltaPct(leads.current, leads.previous)}
        sub={`из ${formatNumber(clicks.current)} кликов`}
        icon={Target}
        accent="info"
      />
      <Tile
        label="Конверсии"
        value={formatNumber(conversions.current)}
        delta={deltaPct(conversions.current, conversions.previous)}
        sub={`из ${formatNumber(leads.current)} лидов`}
        icon={Coins}
        accent="success"
      />
      <Tile
        label="EPC"
        value={formatCents(epcCents.current)}
        delta={deltaPct(epcCents.current, epcCents.previous)}
        sub="доход с клика"
        icon={Wallet}
        accent="warning"
        tooltip="EPC = доход ÷ клики. Чем выше, тем эффективнее источник."
      />
    </div>
  );
}
